import type { LeaguePublic, Schemas } from "@softmax/observatory-client/types";

import { backendFetchResponse } from "@softmax/observatory-client/transport";
import { type GamePageContext } from "../../game-types";

type LeagueResolution =
  | {
      status: "ok";
      league: LeaguePublic;
    }
  | {
      status: "not-found";
    }
  | {
      status: "unavailable";
    };

export async function readGameLeague({
  signal,
  id,
  isPublic,
}: {
  signal?: AbortSignal | undefined;
  id: string;
  isPublic: boolean;
}): Promise<LeagueResolution> {
  const response = await backendFetchResponse(
    "/api/observatory",
    `/v2/leagues/${encodeURIComponent(id)}`,
    {
      headers: { "X-Use-Elevated-Privileges": "false" },
      credentials: isPublic ? "omit" : "same-origin",
      signal: signal ?? null,
    },
  );

  if (response.status === 404 || response.status === 403)
    return { status: "not-found" };

  if (!response.ok) return { status: "unavailable" };

  return { status: "ok", league: await response.json() };
}

export async function resolveGameLeague({
  signal,
  publicAddress,
  context = null,
}: {
  signal?: AbortSignal | undefined;
  publicAddress: string;
  context?: GamePageContext | null;
}): Promise<LeagueResolution> {
  const raw = publicAddress.replace(/^\/?unlisted\//, "").replace(/^\//, "");
  const [slug, leagueSlug, ...tail] = raw.split("/");

  if (!slug || tail.length) return { status: "not-found" };

  const sameGame =
    context && (slug === context.address.slug || slug === context.corpusSlug);

  if (
    sameGame &&
    context.league &&
    (leagueSlug ?? null) === context.league.requestedSlug
  ) {
    return readGameLeague({
      signal,
      id: context.league.id,
      isPublic: context.league.public,
    });
  }

  if (sameGame && context.mode === "upload") return { status: "not-found" };

  const query = new URLSearchParams();

  if (leagueSlug) query.set("league", leagueSlug);

  const lookupSlug = sameGame ? context.address.slug : slug;
  const response = await backendFetchResponse(
    "/api/observatory",
    `/v2/game-addresses/${encodeURIComponent(lookupSlug)}/routing${query.size ? `?${query}` : ""}`,
    {
      headers: { "X-Use-Elevated-Privileges": "false" },
      credentials: "same-origin",
      signal: signal ?? null,
    },
  );

  if (response.status === 404 || response.status === 403)
    return { status: "not-found" };

  if (!response.ok) return { status: "unavailable" };

  const resolution: Schemas["GameAddressRouting"] = await response.json();
  return resolution.league
    ? readGameLeague({
        signal,
        id: resolution.league.id,
        isPublic: resolution.league.public,
      })
    : { status: "not-found" };
}
